import { Check, Loader2, Pencil, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Props {
  chapterId: string;
  /** Current title; controlled by the parent so the optimistic UI matches. */
  currentTitle: string;
  /**
   * Called after a successful PATCH with the updated chapter — parent
   * should `mutate()` its SWR key for the chapters list so the new title
   * lands in the cache.
   */
  onSaved?: (newTitle: string) => void;
  /** When false, the affordance hides entirely. */
  enabled?: boolean;
  /** When true, the pencil sits inline with the title (admin row);
   *  when false, it appears on hover (picker row). */
  alwaysVisible?: boolean;
}

/**
 * Small pencil → input → save row for fixing a curriculum chapter's title
 * in place. PATCH only touches `chapterTitle` per CURRICULUM_API.md §3
 * (the partial-update rule); every other field on the chapter is left
 * exactly as it was.
 *
 * Used from both the CurriculumPicker (chapter dropdown row) and the
 * Curriculum admin page (chapter table row). Optimistically calls
 * `onSaved` after the network round-trip succeeds; the parent triggers
 * SWR `mutate()` so the cache reflects the new value.
 */
export function InlineChapterTitleEdit({
  chapterId,
  currentTitle,
  onSaved,
  enabled = true,
  alwaysVisible = false,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentTitle);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(currentTitle);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [editing, currentTitle]);

  if (!enabled) return null;

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === currentTitle) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await api.curriculum.updateChapter(chapterId, {
        chapterTitle: trimmed,
      });
      onSaved?.(updated.chapterTitle);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(currentTitle);
    setEditing(false);
    setError(null);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setEditing(true);
        }}
        title="Edit chapter title"
        className={cn(
          'grid h-5 w-5 place-items-center rounded text-fg-subtle transition hover:bg-bg-subtle hover:text-accent',
          !alwaysVisible && 'opacity-0 group-hover:opacity-100',
        )}
      >
        <Pencil size={11} />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void save();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        disabled={saving}
        className="h-7 min-w-[200px] flex-1 rounded border border-accent bg-white px-2 text-[12px] text-fg focus:outline-none focus:ring-2 focus:ring-accent/30"
      />
      <button
        type="button"
        onClick={save}
        disabled={saving}
        title="Save (Enter)"
        className="grid h-6 w-6 place-items-center rounded bg-emerald-100 text-emerald-700 transition hover:bg-emerald-200 disabled:opacity-50"
      >
        {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={saving}
        title="Cancel (Esc)"
        className="grid h-6 w-6 place-items-center rounded bg-bg-subtle text-fg-muted transition hover:bg-border disabled:opacity-50"
      >
        <X size={11} />
      </button>
      {error && (
        <span className="text-[11px] text-red-600" title={error}>
          {error.length > 20 ? `${error.slice(0, 20)}…` : error}
        </span>
      )}
    </div>
  );
}
